import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCart } from '../commerce/CartContext';
import { formatCurrency, products as fallbackProducts } from '../data';
import { getProductBySlug } from '../services/catalogueService';
import TryOnBooking from '../components/TryOnBooking';
import ProductReviews from '../components/ProductReviews';

const { FiCheck } = FiIcons;

function getVariantStock(color) {
  if (!color?.inventory) {
    return 99;
  }
  return Math.max(
    0,
    Number(color.inventory.quantity_on_hand || 0)
      - Number(color.inventory.quantity_reserved || 0)
      - Number(color.inventory.quantity_committed || 0)
  );
}

function ProductGallery({ media, productName }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [imgError, setImgError] = useState(false);
  const activeMedia = media[activeIndex] || media[0];

  useEffect(() => {
    setActiveIndex(0);
    setImgError(false);
  }, [media]);

  if (!activeMedia) {
    return null;
  }

  const isVideo = activeMedia.media_type === 'video';

  return (
    <div className="product-gallery">
      <div className="product-gallery-main">
        {isVideo ? (
          <video
            src={activeMedia.media_url}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img
            src={activeMedia.media_url}
            alt={activeMedia.alt_text || `${productName} saree`}
            onError={() => setImgError(true)}
            style={imgError ? { visibility: 'hidden' } : undefined}
          />
        )}
      </div>
      {media.length > 1 && (
        <div className="product-gallery-nav" aria-label="Product photos">
          {media.map((item, index) => (
            <button
              type="button"
              key={item.id || `${item.media_url}-${index}`}
              className={index === activeIndex ? 'active' : ''}
              onClick={() => {
                setActiveIndex(index);
                setImgError(false);
              }}
              aria-label={`View product photo ${index + 1}`}
              aria-pressed={index === activeIndex}
            >
              {item.media_type === 'video' ? (
                <video src={item.media_url} muted />
              ) : (
                <img
                  src={item.media_url}
                  alt={item.alt_text || ''}
                  loading="lazy"
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ColorSelector({ colors, selectedId, onSelect }) {
  if (!colors.length) {
    return null;
  }

  const selected = colors.find((color) => color.id === selectedId);

  return (
    <div className="color-selector">
      <span className="field-label">
        Color — {selected?.name || 'Select a color'}
      </span>
      <div className="color-swatches">
        {colors.map((color) => {
          const stock = getVariantStock(color);
          const isSelected = selectedId === color.id;
          return (
            <button
              type="button"
              key={color.id}
              className={[
                'color-swatch-btn',
                isSelected ? 'selected' : '',
                stock === 0 ? 'out-of-stock' : ''
              ].filter(Boolean).join(' ')}
              onClick={() => onSelect(color.id)}
              disabled={stock === 0}
              title={`${color.name}${stock === 0 ? ' (Out of stock)' : ''}`}
              aria-pressed={isSelected}
            >
              <span
                className="color-swatch-circle"
                style={{ background: color.colorHex || '#c99a45' }}
              />
              <span className="color-swatch-name">{color.name}</span>
              {isSelected && (
                <span className="color-swatch-check">
                  <SafeIcon icon={FiCheck} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProductPage() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [selectedColorId, setSelectedColorId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let active = true;
    getProductBySlug(slug)
      .then((result) => {
        if (!active) return;
        setProduct(result);
        setSelectedColorId(result?.colorVariants?.[0]?.id || '');
        setState(result ? 'ready' : 'missing');
      })
      .catch(() => {
        if (!active) return;
        const fallback = fallbackProducts.find((item) => item.slug === slug);
        setProduct(fallback || null);
        setState(fallback ? 'fallback' : 'missing');
      });
    return () => {
      active = false;
    };
  }, [slug]);

  const colors = product?.colorVariants || [];
  const selectedColor = colors.find(
    (color) => color.id === selectedColorId
  );

  const displayedMedia = useMemo(() => {
    if (!product) {
      return [];
    }
    if (selectedColor?.media?.length) {
      return selectedColor.media;
    }
    if (selectedColor?.image) {
      return [{
        id: `${selectedColor.id}-primary`,
        media_url: selectedColor.image,
        media_type: 'image',
        alt_text: `${product.name} - ${selectedColor.name}`
      }];
    }
    if (product.media?.length) {
      return product.media;
    }
    return product.image
      ? [{
        id: 'fallback-image',
        media_url: product.image,
        media_type: 'image',
        alt_text: `${product.name} saree`
      }]
      : [];
  }, [product, selectedColor]);

  const galleryMedia = useMemo(() => {
    const highlightVideo = product?.media?.find(
      (item) => item.media_type === 'video'
    );
    if (
      !selectedColor
      || !highlightVideo
      || displayedMedia.some(
        (item) => item.media_url === highlightVideo.media_url
      )
    ) {
      return displayedMedia;
    }
    return [...displayedMedia, highlightVideo];
  }, [product, selectedColor, displayedMedia]);

  if (state === 'loading') {
    return (
      <main className="commerce-page commerce-message">
        Loading product…
      </main>
    );
  }

  if (!product) {
    return (
      <main className="commerce-page empty-state">
        <h1>Product unavailable</h1>
        <p>This saree is not currently available.</p>
        <Link className="gold-button" to="/shop">
          View collection
        </Link>
      </main>
    );
  }

  const hasColors = colors.length > 0;
  const available = product.id ? product.inStock : true;
  const displayPrice = selectedColor?.priceOverride ?? product.price;
  const currentStock = selectedColor
    ? getVariantStock(selectedColor)
    : product.availableStock || 99;
  const canAdd = available && currentStock > 0;

  const selectColor = (colorId) => {
    setSelectedColorId(colorId);
    setQuantity(1);
  };

  const handleAddToCart = () => {
    const cartProduct = {
      ...product,
      image: displayedMedia[0]?.media_url || product.image,
      imageAlt:
        displayedMedia[0]?.alt_text
        || product.imageAlt
        || `${product.name} saree`,
      price: displayPrice,
      availableStock: currentStock
    };
    const variant = selectedColor
      ? {
        id: selectedColor.id,
        name: selectedColor.name,
        price_override: selectedColor.priceOverride
      }
      : null;
    addItem(cartProduct, variant, quantity);
  };

  return (
    <main className="commerce-page product-detail">
      <ProductGallery
        key={selectedColorId || 'default'}
        media={galleryMedia}
        productName={product.name}
      />
      <section className="product-detail-copy">
        <span className="eyebrow">The ZEVI Collection</span>
        <h1>{product.name}</h1>
        {product.id && <ProductReviews productId={product.id} />}
        <p className="product-price">{formatCurrency(displayPrice)}</p>
        {product.description && <p>{product.description}</p>}
        <ColorSelector
          colors={colors}
          selectedId={selectedColorId}
          onSelect={selectColor}
        />
        <label className="field-label quantity-field">
          Quantity
          <input
            type="number"
            min="1"
            max={currentStock || 1}
            value={quantity}
            onChange={(event) => {
              const next = Number(event.target.value);
              setQuantity(Math.max(1, Math.min(next, currentStock || 1)));
            }}
          />
        </label>
        <p
          className={`stock-indicator ${canAdd ? 'available' : 'unavailable'}`}
        >
          {canAdd ? `${currentStock} available` : 'Out of stock'}
        </p>
        <button
          type="button"
          className="gold-button product-add"
          disabled={!canAdd || (hasColors && !selectedColor)}
          onClick={handleAddToCart}
        >
          {canAdd ? 'Add to cart' : 'Out of stock'}
        </button>
        {product.id && <TryOnBooking product={product} />}
      </section>
    </main>
  );
}

export default ProductPage;